import { createContext, useContext, useEffect, useSyncExternalStore, useMemo } from "react";
import { commands } from "@/api/commands";
import { resources, type ResourceName } from "@/api/resources";
import {
  sessionSnapshot,
  subscribeSession,
  restoreSession,
  beginLogin,
  logout,
} from "@/api/session";
import { liveStats } from "@/lib/live";

export type AuthStatus = "unknown" | "authenticated" | "guest";
function useApiState() {
  const session = useSyncExternalStore(subscribeSession, sessionSnapshot);
  useSyncExternalStore(resources.accounts.observe, resources.accounts.version);
  useSyncExternalStore(resources.scripts.observe, resources.scripts.version);
  useSyncExternalStore(resources.backtests.observe, resources.backtests.version);
  const account = resources.accounts.get();
  const backtests = resources.backtests.get();
  const scripts = resources.scripts.get();
  return {
    commands,
    authStatus: session.status,
    user: session.user,
    token: session.tokens?.access_token || null,
    sessionError: session.error,
    dataError:
      Object.values(resources)
        .map((r) => (r.status() === "idle" ? null : r.error()))
        .find(Boolean) || null,
    accounts: account.accounts,
    scripts,
    backtests,
    balancesByAccount: account.balances,
    positionsByAccount: account.positions,
    leveragesByAccount: account.leverages,
    ordersByAccount: account.orders,
    ...liveStats(backtests),
    login: beginLogin,
    logout,
    reconnect() {
      void restoreSession();
      Object.values(resources).forEach((r) => r.refresh());
    },
  };
}
type ApiState = ReturnType<typeof useApiState>;
const ApiContext = createContext<ApiState | null>(null);
export function ApiProvider({ children }: { children: React.ReactNode }) {
  const state = useApiState();
  useEffect(() => {
    void restoreSession();
  }, []);
  return <ApiContext.Provider value={state}>{children}</ApiContext.Provider>;
}
export function useApi(needs: ResourceName[] = []) {
  const context = useContext(ApiContext);
  if (!context) throw new Error("useApi must be used within ApiProvider");
  const key = [...new Set(needs)].sort().join(",");
  useEffect(() => {
    const release = key
      ? key.split(",").map((name) => resources[name as ResourceName].acquire())
      : [];
    return () => release.forEach((fn) => fn());
  }, [key]);
  const ready =
    context.authStatus === "authenticated" && needs.every((name) => resources[name].loaded());
  const connected =
    context.authStatus === "authenticated" &&
    needs.every((name) => resources[name].status() === "live");
  // Ledger views own their scoped order resources through useOrders.
  return useMemo(() => ({ ...context, ready, connected }), [context, ready, connected]);
}
